import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { IFileDto } from '../../../config/fileConfig'
import { DataPromise } from '../../../context/AxiosContext'
import { Button, makeStyles, mergeClasses, MessageBar, shorthands, tokens, Tooltip } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import {
    AddRegular,
    bundleIcon,
    CheckmarkStarburstFilled,
    CheckmarkStarburstRegular,
    NewFilled,
    NewRegular,
} from '@fluentui/react-icons'
import { MainDto } from '../../../config/filePartyConfig'
import * as UUID from 'uuid'
import Navigation, { NavigationState } from '../../../components/Form/Navigation'
import { ListItemDto } from '../../../config/partyConfig'
import useEntity from '../../../hooks/useEntity'
import { DateTime } from 'luxon'
import { compareDate, formatFullNumericDate, parseDate } from '../../../helpers/dateHelper'
import { useSettings } from '../../../hooks/useSettings'
import {
    LegalAidCertificate,
    LegalAidCertificateForm,
} from '../../../components/Form/LegalAidCertificate/LegalAidCertificateForm'

type Props = {
    file: DataPromise<IFileDto>
    parties: DataPromise<MainDto[]>
    certificates: DataPromise<LegalAidCertificate[]>
    reset: () => void
}

export type LegalAidCertificateProps = Props & {
    selected?: CertificateState
    setSelected: (state: CertificateState) => void
    legalAidBoards: DataPromise<ListItemDto[]>
}

export type CertificateState = NavigationState & {
    legalAidBoardPartyId: string
}

const getCertificateDate = (certificate: LegalAidCertificate): DateTime =>
    parseDate(certificate.issued || certificate.applied)
const CheckmarkStarburst = bundleIcon(CheckmarkStarburstFilled, CheckmarkStarburstRegular)
const NewIcon = bundleIcon(NewFilled, NewRegular)

const useStyles = makeStyles({
    alert: {
        ...shorthands.margin(tokens.spacingHorizontalS),
    },
    container: {
        display: 'grid',
    },
    containerColumn: {
        gridTemplateColumns: 'min-content 1fr',
    },
    containerRow: {},
    sidebar: {
        display: 'flex',
        minWidth: 'max-content',
        flexWrap: 'nowrap',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    },
    sidebarColumn: {
        flexDirection: 'column',
    },
    sidebarRow: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    createButtonRow: {
        marginRight: tokens.spacingHorizontalL,
    },
    createButtonColumn: {
        marginLeft: tokens.spacingHorizontalM,
    },
})

export function LegalAidCertificateOverview(props: Props): ReactElement {
    const settings = useSettings()
    const styles = useStyles()
    const [legalAidBoards] = useEntity<ListItemDto[]>(
        () => ({ url: '/registration/legal-aid-certificate/board' }),
        false,
    )
    const { isViewport, viewport } = useViewport()
    const [selected, setSelected] = useState<CertificateState>()
    const useColumns = useMemo(() => isViewport('>=', Viewport.XXL), [viewport])

    const certificates = useMemo(
        () => props.certificates.data?.sort((a, b) => compareDate(getCertificateDate(b), getCertificateDate(a))),
        [props.certificates.data],
    )

    if (!settings?.legalAidCertificate) {
        return (
            <MessageBar intent="info" className={styles.alert}>
                Toevoegingen zijn niet of onjuist geconfigureerd. Neem contact op met support om dit probleem te
                verhelpen.
            </MessageBar>
        )
    }
    return (
        <div className={mergeClasses(styles.container, useColumns ? styles.containerColumn : styles.containerRow)}>
            <div className={mergeClasses(styles.sidebar, useColumns ? styles.sidebarColumn : styles.sidebarRow)}>
                <div>
                    <Tooltip relationship="description" content="Maak een nieuwe toevoeging aan" withArrow>
                        <Button
                            disabled={selected?.isNew}
                            appearance="primary"
                            icon={<AddRegular />}
                            className={mergeClasses(useColumns ? styles.createButtonColumn : styles.createButtonRow)}
                            onClick={() =>
                                setSelected({
                                    id: UUID.v4(),
                                    isNew: true,
                                    isDirty: false,
                                    legalAidBoardPartyId: undefined,
                                })
                            }
                        >
                            Nieuw
                        </Button>
                    </Tooltip>
                </div>
                <Navigation<LegalAidCertificate, CertificateState>
                    entities={certificates}
                    isLoaded={props.certificates.loaded}
                    getEntityKey={entity => entity.id}
                    getEntityId={entity => entity.id}
                    getEntityLabel={entity => formatFullNumericDate(getCertificateDate(entity))}
                    getEntityIcon={entity => (entity.issued ? <CheckmarkStarburst /> : <NewIcon />)}
                    selected={selected}
                    setSelected={setSelected}
                    createSelectedState={entity => ({
                        id: entity.id,
                        isNew: false,
                        isDirty: false,
                        legalAidBoardPartyId: undefined,
                    })}
                    useColumns={useColumns}
                />
            </div>
            <LegalAidCertificateForm
                {...props}
                selected={selected}
                setSelected={setSelected}
                legalAidBoards={legalAidBoards}
            />
        </div>
    )
}
